import axios, { CancelToken, CancelTokenSource } from 'axios';
import { GET, PATCH, POST } from '../../service/HttpMethod';
import {
	AutopilotAction,
	AutopilotActionCommand,
	AutopilotArtifact,
	AutopilotBootstrapPage,
	AutopilotConversationResponse,
	AutopilotRecentActionsPage,
	AutopilotUpdatePage,
	AutomationMode,
	OrganizationAutomationSettings,
	ProjectAutomationSettings,
	SelfDrivingAcknowledgement,
} from './types';

const nestedData = (value: unknown): unknown => {
	if (typeof value !== 'object' || value === null || !('data' in value)) return value;
	const { data } = value as { data?: unknown };
	return data === undefined ? value : data;
};

export const unwrapAutopilotResponse = <T>(response: unknown): T =>
	nestedData(nestedData(response)) as T;

export const createAutopilotCancelSource = (): CancelTokenSource => axios.CancelToken.source();

export const getAutopilotBootstrap = async (
	cursor?: string | number | null,
	limit = 100,
	cancelToken?: CancelToken,
	projectId?: number | null,
	organizationId?: number | null,
	scope?: 'PROJECT' | 'ORGANIZATION',
): Promise<AutopilotBootstrapPage> =>
	unwrapAutopilotResponse<AutopilotBootstrapPage>(
		await GET({
			url: 'users/me/autopilot/bootstrap',
			params: {
				cursor: cursor || undefined,
				limit,
				projectId: projectId || undefined,
				organizationId: organizationId || undefined,
				scope,
			},
			cancelToken,
		}),
	);

export const getAutopilotUpdates = async (
	cursor?: string | null,
	cancelToken?: CancelToken,
	projectId?: number | null,
	organizationId?: number | null,
	scope?: 'PROJECT' | 'ORGANIZATION',
): Promise<AutopilotUpdatePage> =>
	unwrapAutopilotResponse<AutopilotUpdatePage>(
		await GET({
			url: 'users/me/autopilot/updates',
			params: {
				cursor: cursor || undefined,
				projectId: projectId || undefined,
				organizationId: organizationId || undefined,
				scope,
			},
			cancelToken,
		}),
	);

export const getAutopilotRecentActions = async (
	cursor?: string | null,
	limit = 50,
	cancelToken?: CancelToken,
	projectId?: number | null,
	organizationId?: number | null,
	scope?: 'PROJECT' | 'ORGANIZATION',
): Promise<AutopilotRecentActionsPage> =>
	unwrapAutopilotResponse<AutopilotRecentActionsPage>(
		await GET({
			url: 'users/me/autopilot/recent',
			params: {
				cursor: cursor || undefined,
				limit,
				projectId: projectId || undefined,
				organizationId: organizationId || undefined,
				scope,
			},
			cancelToken,
		}),
	);

export const updateProjectAutomationMode = async (
	projectId: number,
	mode: AutomationMode,
	version: number,
	selfDrivingAcknowledgement?: SelfDrivingAcknowledgement,
): Promise<ProjectAutomationSettings> =>
	unwrapAutopilotResponse<ProjectAutomationSettings>(
		await PATCH({
			url: `projects/${projectId}/automation-settings`,
			data: { mode, version, selfDrivingAcknowledgement },
		}),
	);

export const updateOrganizationSelfDriving = async (
	organizationId: number,
	selfDrivingAllowed: boolean,
	version: number,
): Promise<OrganizationAutomationSettings> =>
	unwrapAutopilotResponse<OrganizationAutomationSettings>(
		await PATCH({
			url: `organizations/${organizationId}/automation-settings`,
			data: { selfDrivingAllowed, version },
		}),
	);

const commandAction = async (
	actionId: number | string,
	command: 'approve' | 'dismiss' | 'retry' | 'undo',
	payload: AutopilotActionCommand,
): Promise<AutopilotAction> =>
	unwrapAutopilotResponse<AutopilotAction>(
		await POST({
			url: `autopilot/actions/${actionId}/${command}`,
			data: payload,
		}),
	);

export const approveAutopilotAction = (
	actionId: number | string,
	payload: AutopilotActionCommand,
): Promise<AutopilotAction> => commandAction(actionId, 'approve', payload);

export const dismissAutopilotAction = (
	actionId: number | string,
	payload: AutopilotActionCommand,
): Promise<AutopilotAction> => commandAction(actionId, 'dismiss', payload);

export const retryAutopilotAction = (
	actionId: number | string,
	payload: AutopilotActionCommand,
): Promise<AutopilotAction> => commandAction(actionId, 'retry', payload);

export const undoAutopilotAction = (
	actionId: number | string,
	payload: AutopilotActionCommand,
): Promise<AutopilotAction> => commandAction(actionId, 'undo', payload);

export const commentOnAutopilotAction = async (
	actionId: number | string,
	version: number,
	comment: string,
): Promise<AutopilotAction> =>
	unwrapAutopilotResponse<AutopilotAction>(
		await POST({
			url: `autopilot/actions/${actionId}/comments`,
			data: { version, comment },
		}),
	);

export const markAutopilotActionSeen = async (
	actionId: number | string,
): Promise<AutopilotAction> =>
	unwrapAutopilotResponse<AutopilotAction>(
		await POST({
			url: `autopilot/actions/${actionId}/seen`,
			data: {},
		}),
	);

export const createAutopilotConversation = async (
	content: string,
	projectId?: number | null,
	scope: 'PROJECT' | 'ORGANIZATION' = 'PROJECT',
	cancelToken?: CancelToken,
	organizationId?: number | null,
): Promise<AutopilotConversationResponse> =>
	unwrapAutopilotResponse<AutopilotConversationResponse>(
		await POST({
			url: 'autopilot/conversations',
			data: {
				content,
				projectId: projectId || undefined,
				organizationId: organizationId || undefined,
				scope,
			},
			cancelToken,
		}),
	);

export const sendAutopilotConversationMessage = async (
	conversationId: number | string,
	content: string,
	projectId?: number | null,
	scope: 'PROJECT' | 'ORGANIZATION' = 'PROJECT',
	cancelToken?: CancelToken,
	organizationId?: number | null,
): Promise<AutopilotConversationResponse> =>
	unwrapAutopilotResponse<AutopilotConversationResponse>(
		await POST({
			url: `autopilot/conversations/${conversationId}/messages`,
			data: {
				content,
				projectId: projectId || undefined,
				organizationId: organizationId || undefined,
				scope,
			},
			cancelToken,
		}),
	);

export const getAutopilotArtifact = async (
	artifactId: number | string,
): Promise<AutopilotArtifact> =>
	unwrapAutopilotResponse<AutopilotArtifact>(
		await GET({ url: `autopilot/artifacts/${artifactId}` }),
	);

export const retryAutopilotArtifact = async (
	artifactId: number | string,
): Promise<AutopilotArtifact> =>
	unwrapAutopilotResponse<AutopilotArtifact>(
		await POST({ url: `autopilot/artifacts/${artifactId}/retry`, data: {} }),
	);

export const loadAutopilotArtifact = async (
	artifactId: number | string,
	preview = false,
): Promise<Blob> => {
	const response = await GET({
		url: `autopilot/artifacts/${artifactId}/${preview ? 'preview' : 'download'}`,
		appConfig: { responseType: 'blob' },
	});
	return response.data as Blob;
};

export const loadOwnerFurnishedEquipmentReport = async (
	projectId: number,
	scanRunId: number,
): Promise<Blob> => {
	if (
		!Number.isInteger(projectId) ||
		projectId <= 0 ||
		!Number.isInteger(scanRunId) ||
		scanRunId <= 0
	) {
		throw new Error('A valid owner-furnished report reference is required.');
	}
	const response = await GET({
		url: `projects/${projectId}/owner-furnished-scans/${scanRunId}/report`,
		appConfig: { responseType: 'blob' },
	});
	return response.data as Blob;
};
